import React, { useState, useEffect } from "react";
import { IntakeData } from "../api/client";

const F = "'Roboto Mono', monospace";

const PRODUCTS = ["Lead Generation", "Display", "Content Marketing", "Webcast"] as const;
const REGIONS = ["North America", "EMEA", "APAC", "LATAM", "Global", "I don't know"];
const LEAD_TYPES = ["NQL", "MQL", "HQL", "BANT", "Appointments", "I don't know"];

interface CompactIntakeProps {
  collapsed: boolean;
  intakeData: IntakeData | null;
  onComplete: (data: IntakeData) => void;
  onPartialUpdate: (partial: Partial<IntakeData>) => void;
  onExpand: () => void;
}

export const CompactIntake: React.FC<CompactIntakeProps> = ({
  collapsed,
  intakeData,
  onComplete,
  onPartialUpdate,
  onExpand,
}) => {
  const [clientName, setClientName] = useState(intakeData?.client_name || "");
  const [audienceType, setAudienceType] = useState<"agency" | "client" | "">(
    intakeData?.audience_type || ""
  );
  const [approach, setApproach] = useState<"standard" | "innovation" | "">(
    intakeData?.approach || ""
  );
  const [products, setProducts] = useState<string[]>(intakeData?.products || []);
  const [innovationNotes, setInnovationNotes] = useState(intakeData?.innovation_notes || "");

  // Lead gen basics
  const [leadType, setLeadType] = useState("");
  const [region, setRegion] = useState("");

  useEffect(() => {
    const partial: Partial<IntakeData> = {};
    if (clientName.trim()) partial.client_name = clientName.trim();
    if (audienceType) partial.audience_type = audienceType as "agency" | "client";
    if (approach) partial.approach = approach as "standard" | "innovation";
    if (products.length > 0) partial.products = products;
    if (innovationNotes) partial.innovation_notes = innovationNotes;
    onPartialUpdate(partial);
  }, [clientName, audienceType, approach, products, innovationNotes, onPartialUpdate]);

  const toggleProduct = (p: string) => {
    setProducts((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  const canSubmit =
    clientName.trim() !== "" &&
    audienceType !== "" &&
    approach !== "" &&
    products.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onComplete({
      audience_type: audienceType as "agency" | "client",
      client_name: clientName.trim(),
      approach: approach as "standard" | "innovation",
      innovation_notes: innovationNotes,
      products,
      criteria_set: products.includes("Lead Generation")
        ? { lead_type: leadType, region }
        : undefined,
    });
  };

  // Collapsed: summary bar
  if (collapsed && intakeData) {
    return (
      <div style={styles.collapsed} onClick={onExpand}>
        <div style={styles.collapsedContent}>
          <span style={styles.collapsedClient}>{intakeData.client_name}</span>
          <span style={styles.collapsedMeta}>
            {intakeData.audience_type === "agency" ? "Agency" : "Client"} &mdash;{" "}
            {intakeData.approach === "standard" ? "Standard" : "Innovation"} &mdash;{" "}
            {intakeData.products.join(", ")}
          </span>
        </div>
        <button style={styles.editButton}>EDIT</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.form}>
        {/* Row 1: Client + Audience */}
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>CLIENT</label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="e.g. Dell, NVIDIA..."
              style={styles.input}
              autoFocus
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>AUDIENCE</label>
            <div style={styles.toggleRow}>
              <button
                style={audienceType === "agency" ? styles.toggleActive : styles.toggle}
                onClick={() => setAudienceType("agency")}
              >
                AGENCY
              </button>
              <button
                style={audienceType === "client" ? styles.toggleActive : styles.toggle}
                onClick={() => setAudienceType("client")}
              >
                CLIENT
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: Approach */}
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>APPROACH</label>
            <div style={styles.toggleRow}>
              <button
                style={approach === "standard" ? styles.toggleActive : styles.toggle}
                onClick={() => setApproach("standard")}
              >
                STANDARD
              </button>
              <button
                style={approach === "innovation" ? styles.toggleActive : styles.toggle}
                onClick={() => setApproach("innovation")}
              >
                INNOVATION
              </button>
            </div>
          </div>
          {approach === "innovation" && (
            <div style={styles.field}>
              <label style={styles.label}>NOTES</label>
              <input
                type="text"
                value={innovationNotes}
                onChange={(e) => setInnovationNotes(e.target.value)}
                placeholder="Describe the idea..."
                style={styles.input}
              />
            </div>
          )}
        </div>

        {/* Row 3: Products */}
        <div style={styles.field}>
          <label style={styles.label}>PRODUCTS</label>
          <div style={styles.productRow}>
            {PRODUCTS.map((p) => (
              <button
                key={p}
                style={products.includes(p) ? styles.productActive : styles.product}
                onClick={() => toggleProduct(p)}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Lead Gen quick config */}
        {products.includes("Lead Generation") && (
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>LEAD TYPE</label>
              <select
                value={leadType}
                onChange={(e) => setLeadType(e.target.value)}
                style={styles.select}
              >
                <option value="">Select...</option>
                {LEAD_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>REGION</label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                style={styles.select}
              >
                <option value="">Select...</option>
                {REGIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Submit */}
        <button
          style={canSubmit ? styles.submitButton : styles.submitButtonDisabled}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          NEXT
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: "16px 20px",
    borderBottom: "2px solid #E0E0E0",
    flexShrink: 0,
  },
  collapsed: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 20px",
    borderBottom: "1px solid #E0E0E0",
    background: "#FAFAFA",
    cursor: "pointer",
    flexShrink: 0,
  },
  collapsedContent: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
  },
  collapsedClient: {
    fontSize: 12,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  collapsedMeta: {
    fontSize: 9,
    color: "#666",
  },
  editButton: {
    padding: "4px 10px",
    background: "#fff",
    color: "#1a1a1a",
    border: "1px solid #D0D0D0",
    borderRadius: 4,
    fontSize: 8,
    fontWeight: 700,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.5,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  row: {
    display: "flex",
    gap: 12,
  },
  field: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    color: "#666",
    letterSpacing: 0.4,
  },
  input: {
    padding: "8px 10px",
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    fontSize: 11,
    fontFamily: F,
    outline: "none",
  },
  select: {
    padding: "8px 10px",
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    fontSize: 11,
    fontFamily: F,
    outline: "none",
    background: "#fff",
  },
  toggleRow: {
    display: "flex",
    gap: 0,
  },
  toggle: {
    flex: 1,
    padding: "9px 18px",
    background: "#fff",
    color: "#1a1a1a",
    border: "1px solid #D0D0D0",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.5,
  },
  toggleActive: {
    flex: 1,
    padding: "9px 18px",
    background: "#1a1a1a",
    color: "#fff",
    border: "1px solid #1a1a1a",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.5,
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  },
  productRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap" as const,
  },
  product: {
    padding: "6px 10px",
    background: "#fff",
    color: "#1a1a1a",
    border: "1px solid #D0D0D0",
    borderRadius: 4,
    fontSize: 8,
    fontWeight: 700,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.3,
  },
  productActive: {
    padding: "6px 10px",
    background: "#1a1a1a",
    color: "#fff",
    border: "1px solid #1a1a1a",
    borderRadius: 4,
    fontSize: 8,
    fontWeight: 700,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.3,
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  },
  submitButton: {
    padding: "11px 22px",
    background: "#FF0000",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontWeight: 700,
    fontSize: 11,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.5,
    alignSelf: "flex-end",
    boxShadow: "0 1px 3px rgba(255,0,0,0.2)",
  },
  submitButtonDisabled: {
    padding: "11px 22px",
    background: "#999",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontWeight: 700,
    fontSize: 11,
    fontFamily: F,
    cursor: "not-allowed",
    letterSpacing: 0.5,
    alignSelf: "flex-end",
  },
};
